import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Loader } from "#components/atoms/Loader/Loader";
import { Text } from "#components/atoms/Typographies/Text";
import { NestingSvgStatus } from "#modules/layoutsV2/view/useNestingSvg";
import { ZoomablePanel } from "#modules/layoutsV2/view/ZoomablePanel";
import { Flex } from "#shared/view/components/Flex";

import classes from "./NestingSheetImage.module.css";

type Props = {
  status: NestingSvgStatus;
  markup: string | null;
  svgUrl: string;
  alt: string;
  onImageError: () => void;
  onRetry: () => void;
};

export const NestingSheetImage = ({
  status,
  markup,
  svgUrl,
  alt,
  onImageError,
  onRetry,
}: Props) => {
  const { t } = useTranslation();

  if (status === "loading") {
    return (
      <Flex align="center" justify="center" h={120}>
        <Loader size="large" />
      </Flex>
    );
  }

  if (status === "error") {
    return (
      <Flex direction="column" align="center" gap={spacing[3]} py={spacing[4]}>
        <Text size="small" color={colors.text.errorDefault}>
          {t("layout.sidePanel.svgError")}
        </Text>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t("layout.sidePanel.svgRetry")}
        </Button>
      </Flex>
    );
  }

  if (status === "colored" && markup) {
    return (
      <ZoomablePanel>
        <div
          className={classes.inlineSvg}
          role="img"
          aria-label={alt}
          dangerouslySetInnerHTML={{ __html: markup }}
        />
      </ZoomablePanel>
    );
  }

  return (
    <ZoomablePanel>
      <img
        src={svgUrl}
        alt={alt}
        onError={onImageError}
        draggable={false}
        style={{
          width: "100%",
          height: "auto",
          maxHeight: 400,
          objectFit: "contain",
          display: "block",
        }}
      />
    </ZoomablePanel>
  );
};
